import { GtmClient } from '@photosi/gtm-service';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { DATADOG_CUSTOM_ACTIONS } from 'src/_shared/datadog/DatadogCustomActions';
import Datadog from 'src/_shared/datadog/index.web';
import { UserInstance } from 'src/store/user/user';
import { ICartInfo, ICartItem, ICreatedCartRow } from '@photosi/expo-photosi-sdk/build/Cart';
import braze, { FLUSH_LOG_GRACE_MS, wait } from 'src/_shared/braze';
import { FeatureFlagAmplitude } from 'src/_shared/featureFlag';

export const trackCartAddSuccessEvent = async (
  user: UserInstance,
  cartInfo: ICartInfo,
  createdCartRows: ICreatedCartRow[],
): Promise<void> => {
  const { cartItems = [], vatIncludedFullTotal, orderId } = cartInfo;
  const totalItemsQuantity = cartItems.reduce((acc, item) => acc + item?.quantity, 0);
  const cartTrackingProducts: TrackingProducts = cartItems.map((item: ICartItem) => ({
    currency_code: item.currencyCode,
    product_category_code: item.productCategoryCode,
    product_category_name: item.productCategoryName,
    product_type: item.productType,
    quantity: item.quantity,
    vat_included_formatted_total_price: item.formattedVatIncludedTotalFullPrice,
    vat_included_total_price: item.vatIncludedTotalPrice,
    editorType: item.editorType,
    product_display_name: item.productDisplayName,
    product_name: item.productDisplayName?.toLowerCase().replace(/ /g, '_'),
  }));

  const addedCartItems = createdCartRows
    .map(cartRow => cartItems.find(cartItem => getCartItemId(cartItem) === cartRow.rowGuid))
    .filter((cartItem): cartItem is ICartItem => !!cartItem);

  if (addedCartItems.length === 0) return;

  const userId = String(user.id);
  addedCartItems.forEach(() => Datadog.logInfo(DATADOG_CUSTOM_ACTIONS.web_cart_add_success));

  const isFlagOn = amplitude.getVariantValue(FeatureFlagAmplitude.WEB_BRAZE_CART_ADD_SUCCESS_SDK) === 'on';
  const shouldUseSdk = isFlagOn && (await braze.isSdkReady());

  const amplitudeEvents = addedCartItems.map(({ currencyCode, productCode, productCategoryCode }) =>
    amplitude.trackEvent(AnalyticsEvents.cart.addSuccess, {
      userType: user.userType,
      userId,
      lastProductCategoryCode: productCategoryCode,
      lastProductCurrencyCode: currencyCode,
      lastProductPrice: vatIncludedFullTotal,
      orderId,
      totalProductsQuantity: totalItemsQuantity,
      unirepoLastProductCode: productCode,
      products: cartTrackingProducts,
    }),
  );

  const brazeTracking = shouldUseSdk
    ? trackWithSdk(addedCartItems, userId, orderId)
    : braze.flushAfter(
        addedCartItems.map(cartItem => () => GtmClient.emitEvent(getAddToCartPush(cartItem, userId, orderId, false))),
        undefined,
        isFlagOn ? 'sdk-not-ready' : 'flag-off',
      );

  await Promise.all([brazeTracking, ...amplitudeEvents]);
};

const trackWithSdk = async (addedCartItems: ICartItem[], userId: string, orderId: string): Promise<void> => {
  for (const [index, cartItem] of addedCartItems.entries()) {
    if (index > 0) {
      await wait(FLUSH_LOG_GRACE_MS);
    }
    GtmClient.emitEvent(getAddToCartPush(cartItem, userId, orderId, true));
  }

  await braze.trackEventsAndFlush(
    addedCartItems.map(cartItem => ({
      event: AnalyticsEvents.cart.addSuccess,
      properties: getCartAddSuccessProperties(cartItem, userId, orderId),
    })),
  );
};

type TrackingProducts = {
  currency_code: string;
  product_category_code: string;
  product_category_name: string;
  product_type: string;
  quantity: number;
  vat_included_formatted_total_price: string;
  vat_included_total_price: number;
  editorType: string | null;
  product_display_name: string;
  product_name: string;
}[];

const getCartAddSuccessProperties = (
  cartItem: ICartItem,
  userId: string,
  orderId: string,
): Record<string, string | null | undefined> => {
  const { editorType, productCode, productCategoryCode } = cartItem;

  return {
    'User ID': userId,
    Platform: 'Web',
    'Editor Type': editorType,
    unirepo_product_code: productCode,
    product_category_code: productCategoryCode,
    order_ID: orderId,
    userphotoSi_ID: userId,
  };
};

const getAddToCartPush = (
  cartItem: ICartItem,
  userId: string,
  orderId: string,
  isSentBySdk: boolean,
): Record<string, unknown> => {
  const { editorType, currencyCode, productCode, productCategoryCode } = cartItem;

  return {
    event: 'addToCart',
    braze_sent_by_sdk: isSentBySdk,
    userphotoSi_ID: userId,
    ecommerce: {
      currencyCode: currencyCode || 'EUR',
      add: {
        products: getCartItemId(cartItem),
      },
      editor_type: editorType,
      unirepo_product_code: productCode,
      product_category_code: productCategoryCode,
      order_ID: orderId,
    },
  };
};

function getCartItemId(cartItem: ICartItem): string | undefined {
  return cartItem.id || (cartItem.rowDetails.length >= 1 ? cartItem.rowDetails[0].id : undefined);
}
